import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import api, { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Upload, X, ImageIcon, Wrench, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS_NO_VAT = [
  { key: "forsikring", label: "Forsikring", suffix: "/ md" },
  { key: "vejhjaelp", label: "Vejhjælp", suffix: "/ md" },
  { key: "vaegtafgift", label: "Vægtafgift", suffix: "/ 6 mdr" },
  { key: "laan", label: "Lån", suffix: "/ md" },
];
const FIELDS_VAT = [
  { key: "noeglefri", label: "Nøglefri", suffix: "/ md" },
  { key: "leasing", label: "Leasing", suffix: "/ md" },
];

const ALL_KEYS = [...FIELDS_NO_VAT, ...FIELDS_VAT].map((f) => f.key);

// All editable CMS text fields with their placeholders (current site defaults)
const CMS_TEXT_FIELDS = [
  // Forside hero
  { key: "hero_label", label: "Hero · Lille label", placeholder: "Mahasa", group: "Forside · Hero" },
  { key: "hero_title_line1", label: "Hero · Overskrift linje 1", placeholder: "Den rette bil.", group: "Forside · Hero" },
  { key: "hero_title_line2", label: "Hero · Overskrift linje 2", placeholder: "Når du har brug for den.", group: "Forside · Hero" },
  { key: "hero_subtitle", label: "Hero · Undertekst", placeholder: "Boutique-biludlejning og bilforhandling i hjertet af Danmark. Kvalitet, service og personlig kontakt — fra første dag.", textarea: true, group: "Forside · Hero" },
  { key: "hero_cta_rent_label", label: "Hero · Udlejnings-knap", placeholder: "Se udlejningsbiler", group: "Forside · Hero" },
  { key: "hero_cta_sale_label", label: "Hero · Salgs-knap", placeholder: "Biler til salg", group: "Forside · Hero" },
  // Forside featured
  { key: "featured_label", label: "Udvalgte biler · Lille label", placeholder: "Udvalgte biler", group: "Forside · Udvalgte biler" },
  { key: "featured_title", label: "Udvalgte biler · Overskrift", placeholder: "Klar til at køre.", group: "Forside · Udvalgte biler" },
  // Forside Om-snippet
  { key: "about_snippet_label", label: "Om-snippet · Lille label", placeholder: "Om Mahasa", group: "Forside · Om-snippet" },
  { key: "about_snippet_title", label: "Om-snippet · Overskrift", placeholder: "Bil. Behov. Forbindelse.", group: "Forside · Om-snippet" },
  { key: "about_snippet_text", label: "Om-snippet · Tekst", placeholder: "Vi tror på, at bilen er mere end transport — den er frihed, ansvar, og oplevelser.", textarea: true, group: "Forside · Om-snippet" },
  // Forside CTA
  { key: "cta_label", label: "Bund-CTA · Lille label", placeholder: "Spørgsmål?", group: "Forside · Bund-CTA" },
  { key: "cta_title", label: "Bund-CTA · Overskrift", placeholder: "Vi er klar til at hjælpe.", group: "Forside · Bund-CTA" },
  { key: "cta_subtitle", label: "Bund-CTA · Undertekst", placeholder: "Skriv eller ring — vi vender altid tilbage hurtigt.", textarea: true, group: "Forside · Bund-CTA" },
  // Om os
  { key: "about_title", label: "Om os · Overskrift", placeholder: "Mere end biludlejning.", group: "Om os-siden" },
  // Listings rent
  { key: "rent_title", label: "Udlejning · Overskrift", placeholder: "Find din næste bil.", group: "Udlejnings-siden" },
  { key: "rent_subtitle", label: "Udlejning · Undertekst", placeholder: "Hos Mahasa lejer du kvalitetsbiler med personlig service. Vælg dato — vi gør resten.", textarea: true, group: "Udlejnings-siden" },
  // Listings sale
  { key: "sale_title", label: "Til salg · Overskrift", placeholder: "Biler vi gerne ser videre.", group: "Til-salg-siden" },
  { key: "sale_subtitle", label: "Til salg · Undertekst", placeholder: "Nøje udvalgte biler til salg. Personlig overdragelse og fuld historik følger med.", textarea: true, group: "Til-salg-siden" },
  // Contact
  { key: "contact_title", label: "Kontakt · Overskrift", placeholder: "Skriv til os.", group: "Kontakt-siden" },
  { key: "contact_subtitle", label: "Kontakt · Undertekst", placeholder: "Spørgsmål om en bil, en aftale, eller bare for at sige hej — vi svarer altid hurtigt.", textarea: true, group: "Kontakt-siden" },
];

const CMS_KEYS = CMS_TEXT_FIELDS.map((f) => f.key).concat([
  "hero_image_url", "hero_image_public_id",
  "about_image_url", "about_image_public_id",
]);

const emptyCosts = () => Object.fromEntries(ALL_KEYS.map((k) => [k, 0]));
const emptyCms = () => Object.fromEntries(CMS_KEYS.map((k) => [k, ""]));

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const buildState = (s) => ({
    default_fixed_costs: { ...emptyCosts(), ...(s.default_fixed_costs || {}) },
    default_fixed_costs_lager: { ...emptyCosts(), ...(s.default_fixed_costs_lager || {}) },
    default_fixed_costs_carbuddi: { ...emptyCosts(), ...(s.default_fixed_costs_carbuddi || {}) },
    service_interval_km: s.service_interval_km ?? 15000,
    about_us: s.about_us || "",
    contact_email: s.contact_email || "",
    contact_phone: s.contact_phone || "",
    contact_address: s.contact_address || "",
    contact_hours: s.contact_hours || "",
    cvr: s.cvr || "",
    maintenance_mode: !!s.maintenance_mode,
    maintenance_title: s.maintenance_title || "",
    maintenance_message: s.maintenance_message || "",
    show_admin_button: s.show_admin_button !== false, // default true
    ...Object.fromEntries(CMS_KEYS.map((k) => [k, s[k] || ""])),
  });

  useEffect(() => {
    api.get("/settings")
      .then((r) => setSettings(buildState(r.data || {})))
      .catch((e) => toast.error(formatErr(e)));
  }, []);

  const setRoot = (k, v) =>
    setSettings((s) => ({ ...s, [k]: v }));

  const save = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      const body = {
        default_fixed_costs: Object.fromEntries(
          Object.entries(settings.default_fixed_costs).map(([k, v]) => [k, Number(v || 0)])
        ),
        default_fixed_costs_lager: Object.fromEntries(
          Object.entries(settings.default_fixed_costs_lager).map(([k, v]) => [k, Number(v || 0)])
        ),
        default_fixed_costs_carbuddi: Object.fromEntries(
          Object.entries(settings.default_fixed_costs_carbuddi).map(([k, v]) => [k, Number(v || 0)])
        ),
        service_interval_km: Number(settings.service_interval_km || 15000),
        about_us: settings.about_us || "",
        contact_email: settings.contact_email || "",
        contact_phone: settings.contact_phone || "",
        contact_address: settings.contact_address || "",
        contact_hours: settings.contact_hours || "",
        cvr: settings.cvr || "",
        ...Object.fromEntries(CMS_KEYS.map((k) => [k, settings[k] || ""])),
        maintenance_mode: !!settings.maintenance_mode,
        maintenance_title: settings.maintenance_title || "",
        maintenance_message: settings.maintenance_message || "",
        show_admin_button: settings.show_admin_button !== false,
      };
      const res = await api.put("/settings", body);
      setSettings(buildState(res.data || {}));
      toast.success("Indstillinger gemt");
    } catch (e2) {
      toast.error(formatErr(e2));
    } finally { setSaving(false); }
  };

  if (!settings) return <div className="p-4 sm:p-10 text-zinc-500">Indlæser…</div>;

  // Auto-save just the maintenance fields — friction-free toggle.
  const saveMaintenance = async (patch) => {
    try {
      await api.put("/settings", {
        maintenance_mode: settings.maintenance_mode,
        maintenance_title: settings.maintenance_title || "",
        maintenance_message: settings.maintenance_message || "",
        ...patch,
      });
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  // Group CMS text fields for display
  const groupedCms = CMS_TEXT_FIELDS.reduce((acc, f) => {
    (acc[f.group] = acc[f.group] || []).push(f);
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl" data-testid="settings-page">
      <div className="overline text-zinc-500">Indstillinger</div>
      <h1 className="headline text-3xl sm:text-4xl lg:text-5xl mt-2 mb-2">Indstillinger.</h1>
      <p className="text-zinc-400 text-sm mb-10">
        Vedligehold, hjemmeside-tekster og kontaktinformation. Omkostninger indtastes individuelt direkte på hver bil.
      </p>

      {/* Maintenance mode — instant toggle */}
      <MaintenanceBlock
        settings={settings}
        onChange={(patch) => {
          setSettings((s) => ({ ...s, ...patch }));
          saveMaintenance(patch);
        }}
      />

      {/* Admin-knap på hjemmesiden — instant toggle */}
      <AdminButtonBlock
        on={settings.show_admin_button !== false}
        onChange={async (v) => {
          setSettings((s) => ({ ...s, show_admin_button: v }));
          try {
            await api.put("/settings", { show_admin_button: v });
          } catch (e) {
            toast.error(formatErr(e));
          }
        }}
      />

      {/* Hjemmeside-kontakt (Om os full text + kontaktinfo) */}
      <form onSubmit={save} className="section-card p-6 mt-6">
        <h2 className="font-display text-xl font-bold tracking-tighter text-white mb-1">Hjemmeside · Om os & kontakt</h2>
        <p className="text-xs text-zinc-500 mb-5">Den fulde "Om os"-tekst på siden /om-os, samt kontaktinformation der vises på kontakt-siden.</p>
        <div className="space-y-4">
          <div>
            <Label className="overline">Om os · fuld tekst</Label>
            <textarea
              className="ui-input mt-2 min-h-[140px] rounded-none border-zinc-700"
              value={settings.about_us}
              onChange={(e) => setRoot("about_us", e.target.value)}
              placeholder="Skriv historien om Mahasa — hvem I er, hvad I står for, og hvad kunderne kan forvente…"
              data-testid="settings-about"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="overline">Kontakt email</Label>
              <Input className="rounded-none border-zinc-700 mt-2" value={settings.contact_email} onChange={(e) => setRoot("contact_email", e.target.value)} placeholder="kontakt@mahasa.dk" data-testid="settings-contact-email"/>
            </div>
            <div>
              <Label className="overline">Kontakt telefon</Label>
              <Input className="rounded-none border-zinc-700 mt-2" value={settings.contact_phone} onChange={(e) => setRoot("contact_phone", e.target.value)} placeholder="+45 ..." data-testid="settings-contact-phone"/>
            </div>
            <div>
              <Label className="overline">Adresse</Label>
              <Input className="rounded-none border-zinc-700 mt-2" value={settings.contact_address} onChange={(e) => setRoot("contact_address", e.target.value)} placeholder="Gade, by" data-testid="settings-contact-address"/>
            </div>
            <div>
              <Label className="overline">Åbningstider</Label>
              <Input className="rounded-none border-zinc-700 mt-2" value={settings.contact_hours} onChange={(e) => setRoot("contact_hours", e.target.value)} placeholder="Man-fre 9-17" data-testid="settings-contact-hours"/>
            </div>
            <div>
              <Label className="overline">CVR-nummer</Label>
              <Input className="rounded-none border-zinc-700 mt-2" value={settings.cvr || ""} onChange={(e) => setRoot("cvr", e.target.value)} placeholder="12345678" data-testid="settings-cvr"/>
            </div>
          </div>
        </div>
      </form>

      {/* CMS · Hero & forside billeder */}
      <form onSubmit={save} className="section-card p-6 mt-6">
        <h2 className="font-display text-xl font-bold tracking-tighter text-white mb-1">Hjemmeside · Billeder</h2>
        <p className="text-xs text-zinc-500 mb-5">
          Upload dine egne billeder. Lader du dem stå tomme, bruges de oprindelige standardbilleder.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SingleImageField
            label="Hero-billede (forsiden, stort baggrundsbillede)"
            url={settings.hero_image_url}
            publicId={settings.hero_image_public_id}
            onChange={(u, p) => setSettings((s) => ({ ...s, hero_image_url: u || "", hero_image_public_id: p || "" }))}
            testid="settings-hero-image"
          />
          <SingleImageField
            label='"Om Mahasa"-billede (forsiden + Om os-side)'
            url={settings.about_image_url}
            publicId={settings.about_image_public_id}
            onChange={(u, p) => setSettings((s) => ({ ...s, about_image_url: u || "", about_image_public_id: p || "" }))}
            testid="settings-about-image"
          />
        </div>
      </form>

      {/* CMS · Tekstindhold pr. sektion */}
      <form onSubmit={save} className="section-card p-6 mt-6">
        <h2 className="font-display text-xl font-bold tracking-tighter text-white mb-1">Hjemmeside · Tekstindhold</h2>
        <p className="text-xs text-zinc-500 mb-5">
          Overskriv standardteksten på hver side/sektion. Tomme felter falder tilbage til de viste standarder.
        </p>
        <div className="space-y-8">
          {Object.entries(groupedCms).map(([group, fields]) => (
            <div key={group}>
              <div className="overline text-[#C8A661] mb-3">{group}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.map((f) => (
                  <div key={f.key} className={f.textarea ? "md:col-span-2" : ""}>
                    <Label className="overline">{f.label}</Label>
                    {f.textarea ? (
                      <textarea
                        className="ui-input mt-2 min-h-[88px] rounded-none border-zinc-700 w-full"
                        value={settings[f.key] || ""}
                        onChange={(e) => setRoot(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`cms-${f.key}`}
                      />
                    ) : (
                      <Input
                        className="rounded-none border-zinc-700 mt-2"
                        value={settings[f.key] || ""}
                        onChange={(e) => setRoot(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`cms-${f.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="text-right pt-2 border-t border-zinc-800">
            <Button type="submit" disabled={saving}
              className="rounded-none bg-[#C8A661] text-black hover:bg-[#D4B574] h-11 px-6 text-base"
              data-testid="settings-save-cms-button">
              {saving ? "Gemmer…" : "Gem hjemmesidetekst & billeder"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

function authHdr() {
  const t = localStorage.getItem("mahasa_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function SingleImageField({ label, url, publicId, onChange, testid }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);

  async function handleFile(file) {
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("Billede er over 12 MB"); return; }
    setBusy(true);
    try {
      const sigRes = await axios.get(`${API}/cloudinary/signature?folder=settings`, { headers: authHdr() });
      const sig = sigRes.data;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", sig.api_key);
      fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature);
      fd.append("folder", sig.folder);
      const up = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: "POST", body: fd });
      const json = await up.json();
      if (!json.secure_url) throw new Error(json.error?.message || "Upload fejlede");
      // Delete previous one (best-effort)
      if (publicId) {
        try { await axios.delete(`${API}/cloudinary/${publicId}`, { headers: authHdr() }); } catch { /* ignore */ }
      }
      onChange(json.secure_url, json.public_id);
      toast.success("Billede uploadet — husk at gemme");
    } catch (e) {
      toast.error("Upload fejlede: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function remove() {
    if (publicId) {
      try { await axios.delete(`${API}/cloudinary/${publicId}`, { headers: authHdr() }); } catch { /* ignore */ }
    }
    onChange("", "");
  }

  return (
    <div data-testid={testid}>
      <Label className="overline">{label}</Label>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])}/>
      <div className="mt-2 border border-zinc-700 rounded-none overflow-hidden bg-zinc-900">
        {url ? (
          <div className="relative group aspect-[16/9]">
            <img src={url} alt="" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/55 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-2 bg-white/15 hover:bg-white/25 text-white text-xs rounded flex items-center gap-1" data-testid={`${testid}-replace`}>
                <Upload size={12}/> Skift
              </button>
              <button type="button" onClick={remove} className="px-3 py-2 bg-red-500/80 hover:bg-red-500 text-white text-xs rounded flex items-center gap-1" data-testid={`${testid}-remove`}>
                <X size={12}/> Fjern
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="w-full aspect-[16/9] flex flex-col items-center justify-center gap-2 text-zinc-500 hover:text-amber-500 hover:bg-zinc-800/40 transition disabled:opacity-50"
            data-testid={`${testid}-add`}
          >
            {busy ? (<span className="text-xs">Uploader…</span>) : (<>
              <ImageIcon size={32} strokeWidth={1.2}/>
              <span className="text-xs">Klik for at uploade billede</span>
              <span className="text-[10px] text-zinc-600">Standardbilledet bruges hvis tomt</span>
            </>)}
          </button>
        )}
      </div>
    </div>
  );
}


function MaintenanceBlock({ settings, onChange }) {
  const on = !!settings.maintenance_mode;
  const [titleDraft, setTitleDraft] = useState(settings.maintenance_title || "");
  const [msgDraft, setMsgDraft] = useState(settings.maintenance_message || "");
  const titleTimer = useRef(null);
  const msgTimer = useRef(null);

  const flush = (field, value) => {
    onChange({ [field]: value });
  };

  return (
    <div
      className={`section-card p-6 mb-10 border ${on ? "border-amber-500/60 bg-amber-500/[0.04]" : "border-zinc-700"}`}
      data-testid="maintenance-block"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${on ? "bg-amber-500/20 text-amber-400" : "bg-zinc-800 text-zinc-500"}`}>
          <Wrench size={18} strokeWidth={1.6}/>
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-bold tracking-tighter text-white">Hjemmeside · Vedligeholdelses-tilstand</h2>
              <p className="text-xs text-zinc-500 mt-1 max-w-xl">
                Når slået til vises kun en "vi pudser på siden"-side på mahasa.dk. Admin-panelet er ikke påvirket.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0" data-testid="maintenance-toggle-label">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={on}
                onChange={(e) => onChange({ maintenance_mode: e.target.checked })}
                data-testid="maintenance-toggle"
              />
              <div className="w-12 h-7 bg-zinc-700 rounded-full peer peer-checked:bg-amber-500 transition-colors"></div>
              <div className="absolute left-0.5 top-0.5 w-6 h-6 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
            </label>
          </div>
          {on && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-amber-400" data-testid="maintenance-status">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"/> Aktiv — mahasa.dk er offline
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label className="overline">Overskrift (vises stort på siden)</Label>
          <Input
            className="rounded-none border-zinc-700 mt-2"
            value={titleDraft}
            onChange={(e) => {
              const v = e.target.value;
              setTitleDraft(v);
              if (titleTimer.current) clearTimeout(titleTimer.current);
              titleTimer.current = setTimeout(() => flush("maintenance_title", v), 600);
            }}
            placeholder="Vi pudser på hjemmesiden."
            data-testid="maintenance-title-input"
          />
        </div>
        <div>
          <Label className="overline">Besked (forklar besøgende hvad der sker)</Label>
          <textarea
            className="ui-input mt-2 min-h-[100px] rounded-none border-zinc-700"
            value={msgDraft}
            onChange={(e) => {
              const v = e.target.value;
              setMsgDraft(v);
              if (msgTimer.current) clearTimeout(msgTimer.current);
              msgTimer.current = setTimeout(() => flush("maintenance_message", v), 600);
            }}
            placeholder="Vi er i fuld gang med at gøre mahasa.dk endnu skarpere. Siden er tilbage meget snart — i mellemtiden er du altid velkommen til at kontakte os direkte."
            data-testid="maintenance-message-input"
          />
          <p className="text-[11px] text-zinc-600 mt-1.5">Email & telefon fra "Hjemmeside · Om os & kontakt" vises automatisk som kontaktlinks.</p>
        </div>
      </div>
    </div>
  );
}


function AdminButtonBlock({ on, onChange }) {
  return (
    <div
      className={`section-card p-6 mt-6 border ${on ? "border-zinc-700" : "border-zinc-800"}`}
      data-testid="admin-button-block"
    >
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${on ? "bg-[#C8A661]/20 text-[#C8A661]" : "bg-zinc-800 text-zinc-500"}`}>
          <Lock size={18} strokeWidth={1.6}/>
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-bold tracking-tighter text-white">Hjemmeside · Admin-genvej</h2>
              <p className="text-xs text-zinc-500 mt-1 max-w-xl">
                Lille diskret knap i nederste venstre hjørne på mahasa.dk, der fører til admin-login.
                Slår du den fra, forsvinder knappen — admin er stadig tilgængelig direkte via /login.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0" data-testid="admin-button-toggle-label">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={on}
                onChange={(e) => onChange(e.target.checked)}
                data-testid="admin-button-toggle"
              />
              <div className="w-12 h-7 bg-zinc-700 rounded-full peer peer-checked:bg-[#C8A661] transition-colors"></div>
              <div className="absolute left-0.5 top-0.5 w-6 h-6 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
